import * as cornerstone from "@cornerstonejs/core";
import { metaData } from "@cornerstonejs/core";
import { vec3 } from "gl-matrix";
import {
  makeBytesAvailableInCache,
} from "./CornerstoneService";
import { isMobile } from "react-device-detect";
import {
  PREFETCH_IMAGE_CONCURRENCY,
  PREFETCH_IMAGE_CONCURRENCY_WHILE_INTERACTING,
  PREFETCH_SERIES_DELAY_MS,
} from "../../../constants/Constants";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import { stackPrefetch } from "@cornerstonejs/tools/utilities";
import { isNonImageSeries } from "./seriesModality";
import {
  buildWadorsFrameImageIds,
  fetchAndRegisterMultiFrameMetadata,
  getImageMetadata,
  isInstanceCached,
  prefetchAdditionalFrames,
  resolvePrimaryFrameImageId,
} from "./MultiFrameService";

// Backstop timeout for a single prefetch load. The wadouri loader can HANG on
// the bare, frame-less base URL of an un-split multi-frame instance; without a
// cap the sliding-window worker wedges forever and Promise.all never resolves,
// so the series never reports preload-complete (bar stuck at ~99%). Sized well
// above a normal decode so it only trips on a genuine stall.
const PREFETCH_IMAGE_TIMEOUT_MS = 20000;

// Thumbnails are rendered to a plain <canvas> via CPU (loadImageToCanvas with
// useCPURendering), NOT as Cornerstone WebGL STACK viewports. Each WebGL
// viewport allocates a GPU context, and the browser caps live contexts
// (~8-16). A study with many series/priors used to exhaust the GPU → "WebGL
// context lost" → the patched renderWindow.render() throws → the "Viewer is
// unable to perform the action" alert (seen on macOS/Metal). This registry
// holds each thumbnail's imageIds/stack so the exported viewport-shaped API
// keeps working without a real viewport.
const thumbnailRegistry = new Map();

// Per-thumbnail prefetch abort flags, keyed by thumbnail index.
const prefetchControllers = new Map();

// Registry/controller keys must be type-stable. displayThumbnail stores with a
// NUMBER (React prop index), but drag-and-drop passes a STRING
// (e.dataTransfer.getData("thumbnailIndex")) and some state defaults are strings
// too. Map.get is type-strict, so normalise every key through Number(). The old
// getViewport("thumbnail_"+index) string-concat lookup was type-agnostic, which
// hid this — a strict Map.get would otherwise miss and leave drag-to-viewport
// blank ("1/NaN").
const thumbKey = (index) => Number(index);

// A series is downloaded by up to three independent paths: the thumbnail
// play button, the study-wide background loop in ThumbnailState (both via
// prefetchData), and cornerstone's stackPrefetch on every viewport showing it
// (loadActiveViewport). The thumbnail pause button used to abort only its own
// prefetchData run, so the other two kept downloading the series. A paused
// series is recorded here and every path honours it until the user resumes.
const pausedThumbnails = new Set();

// Viewport elements whose stackPrefetch is currently fetching a given
// thumbnail's series, so pausing that series can stop them too.
const stackPrefetchElements = new Map();

// Thumbnails re-read their play/pause icon whenever preload activity changes.
const preloadListeners = new Set();
const notifyPreloadChange = () => {
  preloadListeners.forEach((listener) => listener());
};

export const subscribePreloadState = (listener) => {
  preloadListeners.add(listener);
  return () => preloadListeners.delete(listener);
};

// Thumbnail indices are reused from study to study, so a pause must not carry
// over into the next study. ThumbnailState calls this when the study changes.
// Viewport bindings are kept, because the new study's first viewport may
// already have bound before this runs. bindViewportStackPrefetch moves an
// element whenever it shows another series; elements whose viewport is gone
// are dropped here.
export const resetThumbnailPreloadState = () => {
  pausedThumbnails.clear();
  for (const elements of stackPrefetchElements.values()) {
    for (const element of elements) {
      if (!element.isConnected) elements.delete(element);
    }
  }
  notifyPreloadChange();
};

// True while any path is downloading this series, including the background
// loop and viewport stackPrefetch, which the button did not start itself.
export const isThumbnailPreloading = (index) => {
  const key = thumbKey(index);
  if (pausedThumbnails.has(key)) return false;
  return (
    prefetchControllers.has(key) ||
    (stackPrefetchElements.get(key)?.size ?? 0) > 0
  );
};

// stackPrefetch.disable clears the whole shared Prefetch request queue, which
// also drops the queued work of every other viewport. Re-enable those so only
// the paused series actually stops (enable skips already-cached images).
const disableStackPrefetch = (element) => {
  try {
    stackPrefetch.disable(element);
  } catch (_) {
    return;
  }
  for (const [key, elements] of stackPrefetchElements) {
    if (pausedThumbnails.has(key)) continue;
    for (const other of elements) {
      if (other === element) continue;
      try {
        stackPrefetch.enable(other);
      } catch (_) {
        // A viewport that has since been torn down just drops out.
      }
    }
  }
};

// Called by loadActiveViewport after setStack. It replaces the old
// unconditional stackPrefetch.enable, so a viewport showing a paused series
// does not start fetching the whole stack again.
export const bindViewportStackPrefetch = (element, thumbnailIndex) => {
  if (!element) return;
  const key = thumbKey(thumbnailIndex);
  // This element now shows a different series; stop tracking it elsewhere.
  for (const [otherKey, elements] of stackPrefetchElements) {
    if (otherKey !== key) elements.delete(element);
  }
  if (pausedThumbnails.has(key)) {
    stackPrefetchElements.get(key)?.delete(element);
    // Clears any listener left over from the series this element showed before.
    disableStackPrefetch(element);
  } else {
    if (!stackPrefetchElements.has(key)) stackPrefetchElements.set(key, new Set());
    stackPrefetchElements.get(key).add(element);
    stackPrefetch.enable(element);
  }
  notifyPreloadChange();
};

// Thumbnail pause button: stop every path that downloads this series.
export const pauseThumbnailPreload = (index) => {
  const key = thumbKey(index);
  pausedThumbnails.add(key);
  const controller = prefetchControllers.get(key);
  if (controller) {
    controller.abort = true;
    prefetchControllers.delete(key);
  }
  const elements = stackPrefetchElements.get(key);
  if (elements) {
    elements.forEach((element) => disableStackPrefetch(element));
    // Their stackPrefetch is off now; a resume goes through prefetchData.
    stackPrefetchElements.delete(key);
  }
  notifyPreloadChange();
};

// Thumbnail play button: clear the pause and start the download. prefetchData
// walks the whole series, so it also covers images stackPrefetch skipped.
export const resumeThumbnailPreload = async (index) => {
  pausedThumbnails.delete(thumbKey(index));
  await prefetchData(index);
};

// --- Preload vs. user input ---------------------------------------------------
// Pointer, key, wheel and touch activity within this window counts as the user
// interacting, which parks the preload lanes above the interaction floor.
const INTERACTION_QUIET_MS = 400;
let lastInteractionAt = -Infinity;
let interactionTrackingInstalled = false;

const installInteractionTracking = () => {
  if (interactionTrackingInstalled || typeof window === "undefined") return;
  interactionTrackingInstalled = true;
  const mark = () => {
    lastInteractionAt = performance.now();
  };
  for (const type of ["pointermove", "pointerdown", "keydown", "wheel", "touchstart"]) {
    window.addEventListener(type, mark, { passive: true, capture: true });
  }
};

const isUserInteracting = () => performance.now() - lastInteractionAt < INTERACTION_QUIET_MS;

// Hands the main thread back before starting the next image, at background
// priority where supported, so pending input (hover, click, menu) runs first.
const yieldToBrowser = () => {
  if (typeof globalThis.scheduler?.postTask === "function") {
    return globalThis.scheduler.postTask(() => {}, { priority: "background" });
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
};

const waitWhileUserInteracts = async (controller) => {
  while (!controller.abort && isUserInteracting()) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
};

// --- Bytes-only preload --------------------------------------------------------
// The background preload of series nobody is looking at only downloads each
// DICOM into the browser's HTTP cache (the backend marks an image's own DICOM
// Cache-Control: private). Nothing is decoded and no pixels are held in RAM;
// when the series is shown, the image loader's request for the same URL is
// answered from that cache and only the decode remains, in the web workers.
// Series shown in a viewport and ones the user asks for (play button, lateral
// load) still decode up front via the normal path.

// Absolute URLs whose bytes are in the browser's HTTP cache.
const bytesPrefetchedUrls = new Set();
const bytesPrefetchListeners = new Set();
// null = not known yet; decided from the first response. A backend that doesn't
// send a cacheable Cache-Control (older build, or browser-cache.hours=0) makes a
// bytes-only fetch a wasted download, so preload falls back to decoding.
let browserCachesImages = null;

export const subscribeBytesPrefetched = (listener) => {
  bytesPrefetchListeners.add(listener);
  return () => bytesPrefetchListeners.delete(listener);
};

// The URL the image loader requests for this imageId: the part after the scheme,
// without the frame parameter (see parseImageId in @cornerstonejs/dicom-image-loader).
// Only single-request schemes; wadors frames go through a different endpoint.
const loaderUrlOf = (imageId) => {
  if (typeof imageId !== "string" || !/^(dicomweb|wadouri):/.test(imageId)) return null;
  let url = imageId.substring(imageId.indexOf(":") + 1);
  const frameIndex = url.indexOf("frame=");
  if (frameIndex !== -1) url = url.substring(0, frameIndex - 1);
  try {
    return new URL(url, window.location.href).href;
  } catch (_) {
    return null;
  }
};

// The URL a wadors frame imageId fetches (one frame per request). Only the
// study Preload button downloads these bytes-only; the background loop keeps
// decoding wadors frame 1 as before.
const wadorsUrlOf = (imageId) => {
  if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) return null;
  try {
    return new URL(imageId.substring("wadors:".length), window.location.href).href;
  } catch (_) {
    return null;
  }
};

export const isInstanceBytesPrefetched = (imageId) => {
  const url = loaderUrlOf(imageId) ?? wadorsUrlOf(imageId);
  return url != null && bytesPrefetchedUrls.has(url);
};

// Every imageId a series needs on screen, cine frames included: a wadors
// multi-frame instance is one request per frame; a wadouri one is a single file
// that already carries all its frames.
const allFrameImageIds = (imageIds, stack) =>
  imageIds.flatMap((baseImageId, i) => {
    const meta = getImageMetadata(baseImageId);
    const frames =
      Number(stack?.images?.[i]?.numberOfFrames) || Number(meta?.numberOfFrames) || 1;
    if (frames < 2 || typeof baseImageId !== "string" || !baseImageId.startsWith("wadors:")) {
      return [baseImageId];
    }
    return (
      buildWadorsFrameImageIds({
        baseImageId,
        numberOfFrames: frames,
        studyId: meta?.studyId,
        studyUid: meta?.studyUid,
        seriesUid: meta?.seriesUid,
        sopUid: meta?.sopUid,
      }) ?? [baseImageId]
    );
  });

const isImageIdPreloaded = (imageId) =>
  isInstanceCached(imageId) || isInstanceBytesPrefetched(imageId);

// Downloads one image into the HTTP cache without keeping it: the body is read
// and dropped chunk by chunk. Same credentials mode as the loader's XHR
// (withCredentials), so the browser files both under the same cache entry.
const fetchBytesIntoHttpCache = async (url, controller) => {
  const response = await fetch(url, { credentials: "include", priority: "low" });
  if (!response.ok || !response.body) {
    response.body?.cancel().catch(() => {});
    return false;
  }
  if (browserCachesImages === null) {
    const cacheControl = response.headers.get("Cache-Control") || "";
    browserCachesImages = /max-age=\d+/.test(cacheControl) && !/no-store/.test(cacheControl);
  }
  const reader = response.body.getReader();
  while (true) {
    if (controller.abort) {
      reader.cancel().catch(() => {});
      return false;
    }
    const { done } = await reader.read();
    if (done) break;
  }
  bytesPrefetchedUrls.add(url);
  bytesPrefetchListeners.forEach((listener) => listener(url));
  return true;
};

// options.bytesOnly: background preload, download without decoding (see above).
// options.allFrames: also every frame of each cine clip (study Preload button).
export const prefetchData = async (index, options) => {
  const key = thumbKey(index);
  const bytesOnly = options?.bytesOnly === true;
  const allFrames = options?.allFrames === true;
  // The user paused this series. The background loop and the auto-start on
  // thumbnail click / drag must leave it alone until the user resumes it.
  if (pausedThumbnails.has(key)) return;
  const data = thumbnailRegistry.get(key);
  if (!data || !Array.isArray(data.imageIds) || data.imageIds.length === 0)
    return;
  // A decoding run for this series (it's on screen, or the user asked) is worth
  // more than a bytes-only one: never cancel it for the background loop.
  if (bytesOnly && prefetchControllers.has(key)) return;
  if (!bytesOnly) makeBytesAvailableInCache();

  // Cancel any in-flight prefetch for this index before starting a new one.
  if (prefetchControllers.has(key)) {
    prefetchControllers.get(key).abort = true;
  }
  const controller = { abort: false };
  prefetchControllers.set(key, controller);
  notifyPreloadChange();

  // Sliding-window loadAndCacheImage — no viewport-based stackPrefetch. Each
  // cached image still emits IMAGE_CACHE_IMAGE_ADDED, so the onImageLoaded
  // listener in Thumbnail.jsx keeps firing for progress.
  //
  // We keep PREFETCH_IMAGE_CONCURRENCY loads IN FLIGHT at all times and start
  // the next imageId the instant one finishes — instead of firing a batch of N
  // and awaiting the WHOLE batch before the next. loadAndCacheImage resolves
  // only after the web-worker DECODE completes (not when the XHR returns), so a
  // batch barrier left the network idle for the full decode time of the slowest
  // image in each batch — the multi-second gaps between request bursts seen even
  // on a single-series study. A sliding window keeps the request lanes and the
  // decode workers saturated with no gaps, and respects the abort flag between
  // every start so a viewer switch still stops it promptly.
  //
  // Each decoded image is still taken in on the main thread, so the window has a
  // floor: lanes below PREFETCH_IMAGE_CONCURRENCY_WHILE_INTERACTING always run,
  // the lanes above it wait while the user is interacting. Preload never stops,
  // it just narrows while the user acts, and every start yields to input first.
  installInteractionTracking();
  const imageIds = allFrames ? allFrameImageIds(data.imageIds, data.stack) : data.imageIds;
  let nextIndex = 0;
  const runLane = async (lane) => {
    while (true) {
      if (lane >= PREFETCH_IMAGE_CONCURRENCY_WHILE_INTERACTING) {
        await waitWhileUserInteracts(controller);
      }
      await yieldToBrowser();
      if (controller.abort || nextIndex >= imageIds.length) return;
      // Load the primary frame URL for multi-frame instances (?frame=1) instead
      // of the bare base — the frame-less base URL can hang the wadouri loader,
      // and ?frame=1 is the same id the viewport caches, so progress counts it.
      const imageId = resolvePrimaryFrameImageId(imageIds[nextIndex++]);

      if (bytesOnly && browserCachesImages !== false) {
        const url = loaderUrlOf(imageId) ?? (allFrames ? wadorsUrlOf(imageId) : null);
        // Already decoded, or already in the HTTP cache: nothing to download
        if (url && (bytesPrefetchedUrls.has(url) || isInstanceCached(imageIds[nextIndex - 1]))) continue;
        if (url) {
          try {
            await fetchBytesIntoHttpCache(url, controller);
          } catch (_) {
            // network/CORS failure: the viewer will request it again when shown
          }
          // Fall through to decoding only if this response showed the cache is off
          if (browserCachesImages !== false) continue;
        }
      }
      // Race the load against a timeout so a stalled load can't wedge this
      // lane (and therefore Promise.all / the whole series preload) forever.
      let timer;
      await Promise.race([
        loadAndCacheImage(imageId).catch(() => {}),
        new Promise((resolve) => {
          timer = setTimeout(resolve, PREFETCH_IMAGE_TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timer);
    }
  };
  const windowSize = Math.min(PREFETCH_IMAGE_CONCURRENCY, imageIds.length);
  const lanes = [];
  for (let lane = 0; lane < windowSize; lane++) {
    lanes.push(runLane(lane));
  }
  await Promise.all(lanes);
  // Drop the finished run so the thumbnail stops reporting it as active. A
  // newer run for the same index may have replaced it; leave that one alone.
  if (prefetchControllers.get(key) === controller) {
    prefetchControllers.delete(key);
    notifyPreloadChange();
  }
};

// --- Study Preload button ------------------------------------------------------
// Preloads one study's series that aren't loaded yet, through the same
// prefetchData lanes, one series at a time with the background loop's spacing.
// The background loop itself is untouched; a series it (or the button) is
// already downloading is waited for, not started twice. Clips get every frame.

const PRELOADABLE = (stack) =>
  stack && !isNonImageSeries(stack) &&
  Array.isArray(stack.imageIds) && stack.imageIds.length > 0;

/**
 * Fraction (0–1) of the given series' images (cine frames included) already
 * loaded, or null when there is nothing to preload (no series with images yet
 * — e.g. the study's series haven't arrived — or only SR/PR/KO/DOC).
 */
export const getStudyPreloadProgress = (entries) => {
  let total = 0;
  let done = 0;
  for (const { stack } of entries) {
    if (!PRELOADABLE(stack)) continue;
    for (const id of allFrameImageIds(stack.imageIds, stack)) {
      total++;
      if (isImageIdPreloaded(id)) done++;
    }
  }
  return total ? done / total : null;
};

const waitWhileSeriesPrefetching = async (key, controller) => {
  while (!controller.abort && prefetchControllers.has(key)) {
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
};

/**
 * entries: [{ index, stack }] — thumbnail index + stack for each series of one
 * study. controller: { abort } — set abort to stop after the current image.
 */
export const preloadStudySeries = async (entries, controller) => {
  for (const { index, stack } of entries) {
    if (controller.abort) return;
    if (!PRELOADABLE(stack)) continue;
    if (allFrameImageIds(stack.imageIds, stack).every(isImageIdPreloaded)) continue;
    const key = thumbKey(index);
    // Collapsed prior studies never register their other series' thumbnails.
    registerThumbnailStack(index, stack);
    // An explicit "preload this study" overrides a per-series pause.
    if (pausedThumbnails.delete(key)) notifyPreloadChange();
    await waitWhileSeriesPrefetching(key, controller);
    await new Promise((resolve) => setTimeout(resolve, PREFETCH_SERIES_DELAY_MS));
    if (controller.abort) return;
    controller.currentKey = key;
    const run = prefetchData(index, { bytesOnly: true, allFrames: true });
    // prefetchData registers its controller synchronously; hand it the stop.
    controller.seriesController = prefetchControllers.get(key);
    await run;
  }
};

/** Stop a preloadStudySeries run (the series in flight stops after its current images). */
export const stopStudyPreload = (controller) => {
  controller.abort = true;
  if (controller.seriesController) controller.seriesController.abort = true;
  const key = controller.currentKey;
  if (key != null && prefetchControllers.get(key) === controller.seriesController) {
    prefetchControllers.delete(key);
    notifyPreloadChange();
  }
};

export const stopPrefetchData = async (thumbnailIndex, onImageLoaded) => {
  const key = thumbKey(thumbnailIndex);
  const controller = prefetchControllers.get(key);
  if (controller) {
    controller.abort = true;
    prefetchControllers.delete(key);
    notifyPreloadChange();
  }
};

// Abort every in-flight prefetch at once. Used when the SharedViewer switches to
// the Advance (MedDream iframe) viewer: the thumbnail panel is hidden, so the
// background preload loop is paused and any batch still downloading is aborted
// mid-flight so the network goes quiet immediately. Aborted images are simply
// re-fetched (and mostly cache-hit) when preload resumes on switch back.
export const stopAllPrefetchData = () => {
  for (const controller of prefetchControllers.values()) {
    controller.abort = true;
  }
  prefetchControllers.clear();
  notifyPreloadChange();
};

// Decoupling backstop. loadActiveViewport reads imageIds via
// getThumbnailViewport(thumbnailIndex) and early-returns if the registry has no
// entry — which happens when a viewport loads a series whose thumbnail hasn't
// lazily rendered yet (priors / multiple studies), leaving the viewport blank
// and unbound from the tool group. Callers seed the registry from the assigned
// series before loadActiveViewport so the lookup never misses.
//
// Idempotent + fill-only-if-missing: never clobbers an entry displayThumbnail
// already created (which holds the same series for that index), so the
// drag-and-drop path — where the dragged thumbnail is already rendered — keeps
// its real entry even if the caller passes a stale closure stack.
//
// The one exception is the same series having GROWN since the entry was drawn
// (an SC capture appended to the "SC DICOM" series): the entry is replaced, or
// a drag/click of that series would load only the images it first had. A stale
// stack is never longer than the entry, so it still can't clobber it.
export const registerThumbnailStack = (thumbnailIndex, stack) => {
  if (!stack || !Array.isArray(stack.imageIds) || stack.imageIds.length === 0)
    return;
  const key = thumbKey(thumbnailIndex);
  const existing = thumbnailRegistry.get(key);
  const grew = existing?.stack?.seriesId != null
    && existing.stack.seriesId === stack.seriesId
    && stack.imageIds.length > (existing.imageIds?.length ?? 0);
  if (!grew && existing && Array.isArray(existing.imageIds) && existing.imageIds.length)
    return;
  thumbnailRegistry.set(key, {
    imageIds: stack.imageIds,
    stack: stack,
  });
};

// A series that GREW in place (an SC capture appended to the "SC DICOM" series):
// unlike registerThumbnailStack this overwrites the entry, so the next load of
// that thumbnail index gets every image, not the ones it had when first drawn.
export const replaceThumbnailStack = (thumbnailIndex, stack) => {
  if (!stack || !Array.isArray(stack.imageIds) || stack.imageIds.length === 0)
    return;
  thumbnailRegistry.set(thumbKey(thumbnailIndex), {
    imageIds: stack.imageIds,
    stack: stack,
  });
};

// Draw the first frame of a grayscale image straight to `canvas` with a linear
// VOI window, bypassing cornerstone's CPU loadImageToCanvas (whose integer LUT
// renders black for float pixel data — see the call site in displayThumbnail).
//
// Returns true when it rendered, false when it declined (color image, missing
// pixel data, or any error) so the caller can fall back to loadImageToCanvas.
const renderGrayscaleThumbnail = async (canvas, imageId) => {
  try {
    const image = await loadAndCacheImage(imageId);
    if (!image || image.color) return false;

    const width = image.columns || image.width;
    const height = image.rows || image.height;
    if (!width || !height) return false;

    const pixelData =
      (image.voxelManager && image.voxelManager.getScalarData
        ? image.voxelManager.getScalarData()
        : null) ||
      (image.getPixelData ? image.getPixelData() : null);
    if (!pixelData || pixelData.length < width * height) return false;

    // If the loader already applied the modality LUT (preScale), the scalar
    // data is in rescaled space — which is the space WindowCenter/WindowWidth
    // are defined in — so apply rescale only when it didn't.
    const prescaled = !!(image.preScale && image.preScale.scaled);
    const slope = prescaled ? 1 : Number(image.slope) || 1;
    const intercept = prescaled ? 0 : Number(image.intercept) || 0;

    const num = (v) => (Array.isArray(v) ? v[0] : v);
    let ww = Number(num(image.windowWidth));
    let wc = Number(num(image.windowCenter));
    // PET pre-scaled to SUV (PTSuvScalingService): the DICOM window is in BQML,
    // thousands of times the SUV pixel range, so it would render the thumbnail
    // black. Fall through to the pixel-range window (min/max are already SUV).
    const suvScaled =
      prescaled && typeof image.preScale?.scalingParameters?.suvbw === "number";
    if (suvScaled) {
      console.warn("::SUV:: thumbnail ignores BQML DICOM window for", imageId);
      ww = NaN;
    }
    if (!Number.isFinite(ww) || ww <= 0 || !Number.isFinite(wc)) {
      // No usable VOI in metadata — derive one from the rescaled pixel range.
      const min = (Number(image.minPixelValue) || 0) * slope + intercept;
      const max = (Number(image.maxPixelValue) || 0) * slope + intercept;
      ww = max - min || 1;
      wc = (max + min) / 2;
    }

    // DICOM linear VOI LUT (PS3.3 C.11.2.1.2).
    const lo = wc - 0.5 - (ww - 1) / 2;
    const scale = 255 / (ww - 1);

    // MONOCHROME1 images display low pixel values as WHITE — the dicom-image-loader
    // flags this on the image object (createImage.js sets invert = photometricInterpretation
    // === 'MONOCHROME1'), and the main viewport's WebGL shader honors it automatically.
    // This manual CPU renderer bypasses that shader, so without this check MONOCHROME1
    // thumbnails render inverted relative to the correctly-displayed main viewport.
    const invert = !!image.invert;

    const buffer = document.createElement("canvas");
    buffer.width = width;
    buffer.height = height;
    const bctx = buffer.getContext("2d");
    const imageData = bctx.createImageData(width, height);
    const out = imageData.data;
    const count = width * height;
    for (let i = 0; i < count; i++) {
      const value = pixelData[i] * slope + intercept;
      let gray;
      if (value <= lo) gray = invert ? 255 : 0;
      else if (value > lo + ww) gray = invert ? 0 : 255;
      else {
        gray = ((value - lo) * scale) | 0;
        if (invert) gray = 255 - gray;
      }
      const o = i * 4;
      out[o] = gray;
      out[o + 1] = gray;
      out[o + 2] = gray;
      out[o + 3] = 255;
    }
    bctx.putImageData(imageData, 0, 0);

    // Match loadImageToCanvas's drawing-buffer convention so the downstream
    // canvas.style sizing in displayThumbnail behaves identically.
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0);
    return true;
  } catch (err) {
    console.debug("[Thumbnail] manual grayscale render failed", imageId, err);
    return false;
  }
};

// ── Per-instance tiles (series panel grid) ──────────────────────────────────
// Each tile shows ONE instance's first frame, drawn on the CPU like the series
// thumbnail (no WebGL context), downscaled and cached as a small JPEG data URL
// so collapsing / re-expanding a series never decodes again. Renders go
// through a small queue so expanding an 86-instance series doesn't fire 86
// image loads + /metadata fetches at once.
const INSTANCE_TILE_MAX_PX = 160;
const INSTANCE_TILE_CONCURRENCY = 4;
// Upper bound on cached tiles (~10–15 KB each), oldest dropped first, so a tab
// that moves through many studies without leaving the viewer stays bounded.
const INSTANCE_TILE_CACHE_MAX = 300;
// Tiles never compete with the image the user is waiting for: the queue holds
// until a viewport has shown its first image (or this long has passed, so a
// viewport with nothing to show can't block the tiles forever).
const INSTANCE_TILE_GATE_TIMEOUT_MS = 5000;
const instanceTileCache = new Map();    // imageId → data URL
const instanceTileInflight = new Map(); // imageId → Promise<data URL | null>
const instanceTileQueue = [];
let instanceTileActive = 0;

let instanceTileGateOpen = false;
let instanceTileGateTimer = null;
let instanceTileGateListener = null;

const disarmInstanceTileGate = () => {
  if (instanceTileGateListener) {
    // STACK_NEW_IMAGE doesn't bubble; a capture listener on document still sees
    // it for every viewport element (tile/thumbnail canvases are detached).
    document.removeEventListener(cornerstone.Enums.Events.STACK_NEW_IMAGE, instanceTileGateListener, true);
    instanceTileGateListener = null;
  }
  clearTimeout(instanceTileGateTimer);
  instanceTileGateTimer = null;
};

const openInstanceTileGate = () => {
  disarmInstanceTileGate();
  if (instanceTileGateOpen) return;
  instanceTileGateOpen = true;
  drainInstanceTileQueue();
};

const armInstanceTileGate = () => {
  disarmInstanceTileGate();
  instanceTileGateOpen = false;
  if (typeof document === "undefined") {
    instanceTileGateOpen = true;
    return;
  }
  instanceTileGateListener = () => openInstanceTileGate();
  document.addEventListener(cornerstone.Enums.Events.STACK_NEW_IMAGE, instanceTileGateListener, true);
};
armInstanceTileGate();

const cacheInstanceTile = (imageId, url) => {
  instanceTileCache.delete(imageId);
  instanceTileCache.set(imageId, url);
  while (instanceTileCache.size > INSTANCE_TILE_CACHE_MAX) {
    instanceTileCache.delete(instanceTileCache.keys().next().value);
  }
};

/**
 * Forget every cached tile and hold new tiles until the next study's first
 * viewport image. Call when the viewer closes.
 */
export const resetInstanceTiles = () => {
  instanceTileCache.clear();
  // Queued tiles belong to grids that are gone; drop them (and their in-flight
  // entries, so the same image can be requested again later).
  instanceTileQueue.length = 0;
  instanceTileInflight.clear();
  armInstanceTileGate();
};

const drainInstanceTileQueue = () => {
  if (!instanceTileGateOpen) {
    // Start the fallback clock on the first tile that has to wait.
    if (instanceTileQueue.length && !instanceTileGateTimer) {
      instanceTileGateTimer = setTimeout(openInstanceTileGate, INSTANCE_TILE_GATE_TIMEOUT_MS);
    }
    return;
  }
  while (instanceTileActive < INSTANCE_TILE_CONCURRENCY && instanceTileQueue.length) {
    const job = instanceTileQueue.shift();
    instanceTileActive++;
    job().finally(() => {
      instanceTileActive--;
      drainInstanceTileQueue();
    });
  }
};

const renderInstanceTileNow = async (imageId) => {
  await ensureWadorsMetadataReady([imageId]);
  // Frame 1 of a multi-frame instance — the same id the viewport / prefetch use.
  const frameId = resolvePrimaryFrameImageId(imageId);
  const work = document.createElement("canvas");
  const rendered = await renderGrayscaleThumbnail(work, frameId);
  if (!rendered) {
    await cornerstone.utilities.loadImageToCanvas({
      canvas: work,
      imageId: frameId,
      useCPURendering: true,
      thumbnail: true,
    });
  }
  if (!work.width || !work.height) return null;
  const scale = Math.min(1, INSTANCE_TILE_MAX_PX / Math.max(work.width, work.height));
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(work.width * scale));
  out.height = Math.max(1, Math.round(work.height * scale));
  out.getContext("2d").drawImage(work, 0, 0, out.width, out.height);
  return out.toDataURL("image/jpeg", 0.85);
};

/** Cached tile image (data URL) for an instance, or undefined if not rendered yet. */
export const getCachedInstanceTile = (imageId) => instanceTileCache.get(imageId);

/**
 * Render (queued, cached) the first frame of one instance for a series-panel
 * tile. Resolves to a small JPEG data URL, or null when the image can't be drawn.
 */
export const renderInstanceThumbnail = (imageId) => {
  if (typeof imageId !== "string" || !imageId) return Promise.resolve(null);
  if (instanceTileCache.has(imageId)) return Promise.resolve(instanceTileCache.get(imageId));
  if (instanceTileInflight.has(imageId)) return instanceTileInflight.get(imageId);
  const promise = new Promise((resolve) => {
    instanceTileQueue.push(() =>
      renderInstanceTileNow(imageId)
        .then((url) => {
          if (url) cacheInstanceTile(imageId, url);
          resolve(url);
        })
        .catch((err) => {
          console.debug("[SeriesPanel] instance tile render failed", imageId, err);
          resolve(null);
        })
        .finally(() => instanceTileInflight.delete(imageId))
    );
    drainInstanceTileQueue();
  });
  instanceTileInflight.set(imageId, promise);
  return promise;
};

export const displayThumbnail = async (
  element,
  index,
  stack,
  stackSize,
  isPriors,
  priorStudyData,
  studyIndex,
  setThumbnailLoaded
) => {
  // Keep imageIds/stack in the registry instead of creating a WebGL viewport.
  thumbnailRegistry.set(thumbKey(index), {
    imageIds: stack.imageIds,
    stack: stack,
  });

  // NOTE: this used to register a per-thumbnail cornerstone.metaData
  // provider here (one new closure per displayThumbnail call, never
  // removed). hardcodedMetaDataProvider only ever answers for "web:"
  // scheme imageIds — real DICOM series (wadouri:/wadors:) always use
  // stack.imageIds, so that provider never fired truthy here; it just
  // grew metaData's global provider list forever, and every later
  // metaData.get() call anywhere in the app (including the per-slice
  // metadata reads in MPR's reconstructability check) had to walk past
  // all of them first. Dropped as dead weight — see registerGlobalMetadataProvider
  // for the one "web:" fallback provider that's actually needed.

  // Defensive filter — Cornerstone's wadouri metaData provider crashes on
  // any undefined/non-string entry in the stack (it calls parseImageId
  // before checking the scheme). See CornerstoneViewportService for the
  // same guard.
  const safeImageIds = Array.isArray(stack.imageIds)
    ? stack.imageIds.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  if (Array.isArray(stack.imageIds) && safeImageIds.length !== stack.imageIds.length) {
    console.warn(
      "[MultiFrame] thumbnail dropped",
      stack.imageIds.length - safeImageIds.length,
      "invalid imageId entries (was length",
      stack.imageIds.length,
      ")"
    );
  }
  if (safeImageIds.length === 0) {
    console.warn("[MultiFrame] thumbnail stack empty after filtering — skipping setStack");
    return;
  }

  // For any wadors:.../frames/1 slot in the stack (un-split multi-frame
  // instances), make sure the DICOMweb /metadata fetch has completed and
  // is registered with the wadors loader BEFORE setStack — otherwise the
  // loader has no rows/cols/bits to decode frame 1 with. The helper is
  // idempotent per SOP UID and returns the cached promise on repeat
  // calls; fetches for different instances run in parallel.
  await ensureWadorsMetadataReady(safeImageIds);

  // Render the first frame to a plain <canvas> on the CPU — no WebGL context,
  // no rendering engine. The canvas lives inside the passed #thumbnail div.
  try {
    let canvas = element.querySelector("canvas");
    if (!canvas) {
      canvas = document.createElement("canvas");
      element.appendChild(canvas);
    }

    // Render frame 1 ourselves for grayscale images instead of going through
    // cornerstone's CPU loadImageToCanvas.
    //
    // Why: the main viewport is a WebGL stack viewport (shader-based VOI), so it
    // renders any image. The thumbnail CPU path goes
    // loadImageToCanvas -> renderToCanvasCPU -> renderGrayscaleImage, which
    // builds an INTEGER-indexed LUT and looks up lut[pixelData[i]]. The
    // dicom-image-loader pre-scales pixel data by the modality LUT
    // (preScale.enabled defaults to true); when RescaleSlope is non-integer
    // (e.g. Philips MR with slope 1.4112…) the rescaled values are non-integer,
    // so the loader stores them in a Float32Array. lut[<float>] on a typed array
    // is `undefined` -> written as 0 alpha for every pixel -> the canvas stays
    // the black fill. The GPU viewport is immune because it windows in-shader.
    // Result: studies with a non-integer RescaleSlope showed all-black
    // thumbnails while the same series looked fine in the viewport.
    //
    // Drawing the grayscale frame directly (apply modality rescale if the loader
    // didn't, then a linear VOI window) sidesteps the broken integer LUT and
    // handles float or integer pixel data. Color images have no such LUT issue,
    // so they keep using loadImageToCanvas.
    // Render the primary frame URL for a multi-frame first-instance (?frame=1),
    // not the bare frame-less base: the base URL HANGS the wadouri loader
    // (→ the 15s displayThumbnail watchdog fires: "timed out after 15000ms",
    // seen on US priors) and is a DIFFERENT cache key than the ?frame=1 the
    // viewport/prefetch warmed — so rendering the base both stalls and
    // re-downloads instead of reusing the warmed image. Same id
    // resolvePrimaryFrameImageId feeds the prefetch path; single-frame and
    // wadors stacks are returned unchanged.
    const thumbnailFrameId = resolvePrimaryFrameImageId(safeImageIds[0]);
    const renderedManually = await renderGrayscaleThumbnail(
      canvas,
      thumbnailFrameId
    );
    if (!renderedManually) {
      await cornerstone.utilities.loadImageToCanvas({
        canvas: canvas,
        imageId: thumbnailFrameId,
        useCPURendering: true,
        thumbnail: true,
      });
    }

    // loadImageToCanvas sets canvas.style.width/height to fixed pixel values
    // (256 / devicePixelRatio) which overflow the fixed-height thumbnail box
    // (75px) — on a retina display that's a ~128px square spilling out. Re-apply
    // the fill sizing AFTER it renders so the canvas scales to the #thumbnail
    // box with the image contained (aspect preserved). The drawing buffer
    // (canvas.width/height) is left untouched so the bitmap stays sharp.
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.objectFit = "contain";
    canvas.style.display = "block";
  } catch (error) {
    console.log("Thumbnail render error for index", index, error);
  }

  // Initial multi-frame preload at thumbnail load is intentionally
  // disabled — it triggered up-front prefetches for EVERY multi-frame
  // instance across the study (incl. priors), saturating the browser's
  // HTTP/1.1 socket pool and causing net::ERR_INSUFFICIENT_RESOURCES.
  // The active instance's frames are still warmed on demand by
  // prefetchFrameImageIds (called from CornerstoneViewportService when
  // the series opens in the main viewport), which is enough for smooth
  // cine play. Re-enabling this would need a coordinated rate limiter.
  // prefetchRemainingFramesForStack(safeImageIds);

  if (isMobile && index === 0) {
    setThumbnailLoaded(true);
    return;
  }

  if (!isPriors) {
    if (index === stackSize - 1) {
      setThumbnailLoaded(true);
    }
  } else {
    if (priorStudyData.length - 1 === studyIndex) setThumbnailLoaded(true);
  }
};

// Walks a stack's imageIds and awaits one /metadata fetch per wadors
// instance. Single-frame (wadouri) slots are skipped — they need no
// metadata bridge. Returns a Promise that resolves when every needed
// metadata entry is in dicomImageLoader.wadors.metaDataManager (or has
// failed and been logged).
const ensureWadorsMetadataReady = async (imageIds) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) return;
  const seenSops = new Set();
  const pending = [];
  for (const imageId of imageIds) {
    if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) continue;
    const meta = getImageMetadata(imageId);
    if (!meta || !meta.studyUid || !meta.seriesUid || !meta.sopUid) continue;
    if (seenSops.has(meta.sopUid)) continue;
    seenSops.add(meta.sopUid);
    pending.push(
      fetchAndRegisterMultiFrameMetadata({
        studyId: meta.studyId,
        studyUid: meta.studyUid,
        seriesUid: meta.seriesUid,
        sopUid: meta.sopUid,
        numberOfFrames: meta.numberOfFrames,
      })
    );
  }
  if (pending.length) {
    await Promise.all(pending);
  }
};

// Walks a stack's imageIds and fires background prefetches for frames
// 2..N of each multi-frame instance. Frame 1 has already been fetched by
// setStack; this fills the cache for cine playback. Fire-and-forget.
const prefetchRemainingFramesForStack = (imageIds) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) return;
  const seenSops = new Set();
  for (const imageId of imageIds) {
    if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) continue;
    const meta = getImageMetadata(imageId);
    if (!meta || !meta.studyUid || !meta.seriesUid || !meta.sopUid) continue;
    if (!Number.isInteger(meta.numberOfFrames) || meta.numberOfFrames < 2) continue;
    if (seenSops.has(meta.sopUid)) continue;
    seenSops.add(meta.sopUid);
    prefetchAdditionalFrames({
      studyId: meta.studyId,
      studyUid: meta.studyUid,
      seriesUid: meta.seriesUid,
      sopUid: meta.sopUid,
      numberOfFrames: meta.numberOfFrames,
    });
  }
};

// Lightweight viewport-shaped shim from the registry. Consumers only read
// `.imageIds` (and the truthy guard), so this keeps the old API working
// without a real Cornerstone viewport.
// `index` is the real registry key. getAllThumbnailViewports returns entries in
// LOAD order, not index order, so callers that need the thumbnail number must
// read it from here rather than infer it from the array position.
const toViewportShim = (index, data) => ({
  imageIds: data.imageIds,
  getImageIds: () => data.imageIds,
  id: "thumbnail_" + index,
  index,
});

export const getThumbnailViewport = async (index) => {
  const key = thumbKey(index);
  const data = thumbnailRegistry.get(key);
  if (!data) return null;
  return toViewportShim(key, data);
};

export const getAllThumbnailViewports = async () => {
  const result = [];
  for (const [index, data] of thumbnailRegistry) {
    result.push(toViewportShim(index, data));
  }
  return result;
};

export const checkIfLateralSeries = async (thumbnailIndex) => {
  const data = thumbnailRegistry.get(thumbKey(thumbnailIndex));
  if (!data || !Array.isArray(data.imageIds) || data.imageIds.length === 0)
    return false;
  const imageId = await loadAndCacheImage(data.imageIds[0]);
  const imagePlane = metaData.get("imagePlaneModule", imageId.imageId);
  if (imagePlane && imagePlane.rowCosines && imagePlane.columnCosines) {
    const row = [
      Math.round(imagePlane.rowCosines[0]),
      Math.round(imagePlane.rowCosines[1]),
      Math.round(imagePlane.rowCosines[2])
    ];
    const column = [
      Math.round(imagePlane.columnCosines[0]),
      Math.round(imagePlane.columnCosines[1]),
      Math.round(imagePlane.columnCosines[2])
    ];
    const normal = [];
    vec3.cross(normal, column, row);

    if (Math.abs(normal.x) === 1)
      return true;
  }
  return false;
};
